import { Link } from "react-router-dom";
import { TrendingUp, Zap, PlayCircle, Award, LifeBuoy, Rocket } from "lucide-react";
import { useDash, courseProgress } from "@/components/dashboard/Layout";
import { Card, Stat, StatusPill, STATUS } from "@/components/dashboard/kit";
import { inr } from "@/data/content";

const STEPS = [
  ["Get approved", "Our team confirms your payment and activates your ID."],
  ["Learn at your pace", "Finish short lessons and practical tasks from home."],
  ["Earn your certificate", "Complete every lesson in a course to unlock it."],
  ["Share and grow", "Invite friends with your partner link and earn commission."],
];

export default function Overview() {
  const { user, pkg, courses, progress } = useDash();
  const stats = courses.map((c) => courseProgress(progress, c));
  const done = stats.reduce((a, s) => a + s.done, 0);
  const total = stats.reduce((a, s) => a + s.total, 0) || 1;
  const pct = Math.round((done / total) * 100);
  const certs = stats.filter((s) => s.pct === 100).length;

  return (
    <div data-testid="dashboard-overview">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Salaam, <span className="neon-text">{user.name.split(" ")[0]}</span></h1>
          <p className="text-sm text-[var(--muted)] mt-1">Har din thoda seekho, har din aage badho.</p>
        </div>
        <StatusPill status={user.status} />
      </div>
      {user.status !== "approved" && <Card className="mb-6 text-sm text-[var(--muted)]" data-testid="status-note">{STATUS[user.status].text}</Card>}

      <div className="grid xl:grid-cols-[1.6fr_1fr] gap-6">
        <Card>
          <div className="flex items-center gap-3"><div className="icon-orb"><TrendingUp size={20} /></div><div><h2 className="font-display text-lg font-semibold">Learning Progress</h2><p className="text-xs text-[var(--muted)]">{pkg ? `${pkg.name} · ${inr(pkg.price)}` : "No package selected"}</p></div></div>
          <div className="rounded-2xl border border-[var(--surface-border)] p-5 mt-6">
            <div className="flex justify-between text-sm font-semibold"><span>Overall completion</span><span data-testid="overall-progress" className="text-[var(--pink)]">{pct}%</span></div>
            <div className="h-2.5 rounded-full bg-[var(--bg-2)] mt-3 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: "var(--btn-bg)" }} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Stat testid="stat-courses" label="Courses" value={courses.length} />
            <Stat testid="stat-lessons" label="Lessons done" value={done} />
            <Stat testid="stat-certificates" label="Certificates" value={certs} />
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3"><div className="icon-orb"><Zap size={20} /></div><h2 className="font-display text-lg font-semibold">Shortcuts</h2></div>
          <div className="space-y-3 mt-6">
            {[["/dashboard/courses", "Continue learning", PlayCircle], ["/dashboard/certificates", "My certificates", Award], ["/dashboard/partner", "Partner panel", Rocket], ["/dashboard/support", "Need help?", LifeBuoy]].map(([to, l, I]) => (
              <Link key={to} to={to} data-testid={`shortcut-${to.split("/").pop()}`} className="flex items-center gap-3 rounded-2xl border border-[var(--surface-border)] px-4 py-3 text-sm font-semibold hover:border-[var(--pink)] transition-colors"><I size={17} className="text-[var(--pink)]" /> {l}</Link>
            ))}
          </div>
        </Card>
      </div>

      <h2 className="font-display text-2xl font-bold mt-12 mb-5">Your path at <span className="neon-text">Ilmora</span></h2>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {STEPS.map(([t, d], i) => (
          <Card key={t} className="neon-card">
            <span className="font-display text-3xl font-bold neon-text">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-semibold mt-3">{t}</h3><p className="text-xs text-[var(--muted)] mt-1">{d}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
